import React, { useEffect, useState } from 'react';
import { List, ListItem, ListItemText } from '@mui/material';
import Grid from '@mui/material/Grid';
import { useAuth, useRequest } from 'shared/hooks';
import {
  addEmployeeToGroups,
  deleteUserGroupEmployee,
  getEmployeeNonMemberUserGroups,
  getEmployeeUserGroups,
  updateUserGroupEmployee,
} from 'shared/api/admin';
import { AsyncMultipleSelect } from 'shared/components/select/SearchableSelects';
import { userGroupEntity } from 'shared/entityDetails';
import { InfoModal } from 'shared/components/abstract/interactables';
import { BUTTON_COLORS } from 'shared/constants';
import { GridWrapper } from 'shared/components/data-display/GridFormatter';
import UserGroupTable from './UserGroupTable';

/**
 * @param {Object} props
 * @param {Object} props.state
 * @param {number} props.routeId
 * @param {boolean} props.isProfile
 */
export default function UserGroups(props) {
  const {
    state: { data: user },
    routeId,
    isProfile,
  } = props;

  const [stagedAddGroups, setStagedAddGroups] = useState([]);
  const [updateCount, setUpdateCount] = useState(0);
  const [userGroups, setUserGroups] = useState([]);

  const {
    user: currentLoggedInUser,
    isAdmin,
  } = useAuth();
  const {
    request,
    isLoading,
  } = useRequest();

  const userId = routeId || currentLoggedInUser.id;

  useEffect(() => {
    (async () => {
      const result = await request(getEmployeeUserGroups(userId));
      if (result.length > 0) {
        let userGroupsResult = result;
        // reduce the results from the employee search
        userGroupsResult = userGroupsResult.reduce((ugr, group) => {
          const employeeArray = group.user_group_employees;
          const matchedGroupEmployee = employeeArray.find(
            (employee) => employee.employee_id === JSON.parse(userId),
          );
          if (matchedGroupEmployee) {
            // modify each group to contain the current employee
            ugr.push({
              ...group,
              currentEmployee: matchedGroupEmployee,
            });
          }
          return ugr;
        }, []);
        setUserGroups(userGroupsResult);
      } else {
        setUserGroups([]);
      }
    })();
  }, [isAdmin, request, userId, updateCount]);

  const forceUpdate = () => setUpdateCount(updateCount + 1);

  const addGroups = async () => {
    if (stagedAddGroups.length > 0) {
      const addEmployeesId = stagedAddGroups.map((group) => group.id);
      const result = await request(addEmployeeToGroups({
        group_ids: addEmployeesId,
        employee_id: parseInt(userId, 10),
        created_by_id: currentLoggedInUser.id,
      }), 'Added employee to user group');
      if (result) {
        setStagedAddGroups([]);
        forceUpdate();
      }
    }
    return null;
  };

  const deleteUserGroupEmployeeCall = async (employee) => {
    const result = await request(deleteUserGroupEmployee(employee), 'Removed employee from user group');
    if (result) {
      forceUpdate();
    }
  };

  const updateUserGroupEmployeeCall = async (id, body) => {
    const result = await request(updateUserGroupEmployee(id, body), 'Updated employee');
    if (result) {
      forceUpdate();
    }
  };

  return (
    <GridWrapper size={12} title="User Groups">
      {!isProfile && (
        <Grid
          container
          size={12}
          spacing={1}
          sx={{
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <Grid size={10}>
            <AsyncMultipleSelect
              searchRequest={getEmployeeNonMemberUserGroups}
              getOptionLabel={userGroupEntity.getOptionLabel}
              getOptionValue={userGroupEntity.getOptionValue}
              onChange={setStagedAddGroups}
              label="Add Group"
              selectedOptions={stagedAddGroups}
              routeParam={String(userId)}
              updateCount={updateCount}
            />
          </Grid>
          <Grid>
            <InfoModal
              title="Add Group"
              actionName="Add"
              description={(
                <>
                  {`This will add ${user.fullname} to:`}
                  <List
                    sx={{
                      listStyleType: 'disc',
                      pl: 4,
                    }}
                    dense
                  >
                    {stagedAddGroups.map((group) => (
                      <ListItem sx={{ display: 'list-item' }} disablePadding key={group.id}>
                        <ListItemText
                          primary={group.group_name}
                        />
                      </ListItem>
                    ))}
                  </List>
                  Are you sure?
                </>
              )}
              actionCallback={addGroups}
              color={BUTTON_COLORS.primary}
              disabled={isLoading || stagedAddGroups.length < 1 || !isAdmin}
            />
          </Grid>
        </Grid>
      )}
      <UserGroupTable
        currentGroups={userGroups}
        removeFromUserGroup={deleteUserGroupEmployeeCall}
        updateEmployeeFromUserGroup={updateUserGroupEmployeeCall}
        isAdmin={isAdmin}
        readOnly={isProfile}
      />
    </GridWrapper>
  );
}
